import { useEffect, useState } from "react";
import api from "../api/api";
import {
  countUnreadNotifications,
  normalizeNotification,
} from "../utils/notifications";

function useNotifications(currentUser) {
  const [notifications, setNotifications] = useState([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [notificationsError, setNotificationsError] = useState("");

  const currentUserId = Number(
    currentUser?.userId || currentUser?.id || currentUser?.Id,
  );
  const currentUserToken =
    currentUser?.token ||
    currentUser?.accessToken ||
    currentUser?.jwtToken ||
    "";

  useEffect(() => {
    let isMounted = true;

    async function fetchNotifications() {
      if (!currentUserId) {
        if (isMounted) {
          setNotifications([]);
          setNotificationsError("");
          setIsLoadingNotifications(false);
        }
        return;
      }

      try {
        setIsLoadingNotifications(true);
        setNotificationsError("");

        const response = await api.getNotifications(
          { UserId: currentUserId },
          currentUserToken,
        );
        const rawNotifications = Array.isArray(response?.data)
          ? response.data
          : response?.data?.notifications || response?.data?.data || [];

        if (!isMounted) {
          return;
        }

        setNotifications(
          rawNotifications
            .map((notification, index) =>
              normalizeNotification(notification, index),
            )
            .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate)),
        );
      } catch (error) {
        console.error("Failed to load notifications", error);

        if (isMounted) {
          setNotifications([]);
          setNotificationsError(
            error?.response?.data?.message ||
              error?.message ||
              "Failed to load notifications.",
          );
        }
      } finally {
        if (isMounted) {
          setIsLoadingNotifications(false);
        }
      }
    }

    fetchNotifications();

    return () => {
      isMounted = false;
    };
  }, [currentUserId, currentUserToken]);

  const markNotificationAsRead = async (notificationId) => {
    if (!notificationId || !currentUserToken) {
      return;
    }

    setNotifications((prev) =>
      prev.map((notification) =>
        Number(notification.id) === Number(notificationId)
          ? { ...notification, unread: false }
          : notification,
      ),
    );

    try {
      await api.markNotificationAsRead(notificationId, currentUserToken);
    } catch (error) {
      console.error("Failed to mark notification as read", error);
    }
  };

  const markAllNotificationsAsRead = async () => {
    if (!currentUserToken) {
      return;
    }

    setNotifications((prev) =>
      prev.map((notification) => ({ ...notification, unread: false })),
    );

    try {
      await api.markAllNotificationsAsRead(currentUserToken);
    } catch (error) {
      console.error("Failed to mark all notifications as read", error);
    }
  };

  return {
    notifications,
    unreadNotificationCount: countUnreadNotifications(notifications),
    isLoadingNotifications,
    notificationsError,
    markNotificationAsRead,
    markAllNotificationsAsRead,
  };
}

export default useNotifications;
